/**
 * TextCommentInput — Text input area for typed feedback comments.
 *
 * Allows users to add text-based comments as an alternative to voice
 * recording. Submits on button click or Enter key (Shift+Enter for newline).
 */
import { useState, useRef } from "react";

interface TextCommentInputProps {
  readonly onSubmit: (text: string) => void;
  readonly disabled: boolean;
}

export function TextCommentInput({
  onSubmit,
  disabled,
}: TextCommentInputProps) {
  const [text, setText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const trimmed = text.trim();
  const canSubmit = trimmed.length > 0 && !disabled;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(trimmed);
    setText("");
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="fb-text-comment-input" id="feedback-text-input">
      <textarea
        ref={textareaRef}
        className="fb-text-comment-textarea"
        placeholder="Type your feedback here…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        rows={2}
        aria-label="Text feedback"
      />
      <button
        type="button"
        className="fb-text-comment-submit"
        onClick={handleSubmit}
        disabled={!canSubmit}
        aria-label="Add comment"
        title="Add text comment"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M1.5 1.5L14.5 8L1.5 14.5V9.5L10 8L1.5 6.5V1.5Z" />
        </svg>
      </button>
    </div>
  );
}
